// Keeps imported PDF underlays crisp when zoomed in. The import-time raster is a
// fixed resolution, so it pixelates past a certain zoom. Here we re-rasterise the
// *visible* slice of the page through pdf.js at the current on-screen pixel
// density and hand it back as an overlay drawn over the base raster.
//
// It is debounced (so continuous zoom/pan doesn't thrash), capped in pixels (so
// memory stays bounded), and keyed on region+scale so it settles to a no-op once
// the view stops moving. Rotated underlays fall back to the base raster.

import type { BackgroundImage } from '../shared/types';
import type { Viewport } from './viewport';

const MAX_CROP = 3000; // px cap per side of a re-rasterised crop
const DEBOUNCE_MS = 130;

/** A crisp crop covering a world rectangle (centre x,y + size mm). */
export interface SharpCrop { canvas: HTMLCanvasElement; x: number; y: number; widthMm: number; heightMm: number; key: string; }

const crops = new Map<string, SharpCrop>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const docs = new Map<string, Promise<any>>(); // pdfData (base64) → PDFDocumentProxy

export function sharpCropFor(id: string): SharpCrop | null { return crops.get(id) ?? null; }
/** Drop every cached crop (e.g. when a new project loads). */
export function clearSharpCrops(): void { crops.clear(); docs.clear(); }

export interface SharpenItem { bi: BackgroundImage; baseNaturalW: number }

let timer: ReturnType<typeof setTimeout> | null = null;
let running = false;

/** Schedule a (debounced) re-rasterisation pass for the current view. */
export function scheduleSharpen(items: SharpenItem[], vp: Viewport, vw: number, vh: number, onReady: () => void): void {
  if (timer != null) clearTimeout(timer);
  timer = setTimeout(() => { timer = null; void runSharpen(items, vp, vw, vh, onReady); }, DEBOUNCE_MS);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getDoc(pdfData: string): Promise<any> {
  let d = docs.get(pdfData);
  if (!d) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pdfjs = (window as unknown as { pdfjsLib?: any }).pdfjsLib;
    const bytes = Uint8Array.from(atob(pdfData), (c) => c.charCodeAt(0));
    d = pdfjs.getDocument({ data: bytes }).promise;
    docs.set(pdfData, d!);
  }
  return d!;
}

async function runSharpen(items: SharpenItem[], vp: Viewport, vw: number, vh: number, onReady: () => void): Promise<void> {
  if (running) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!(window as unknown as { pdfjsLib?: any }).pdfjsLib) return;
  running = true;
  let changed = false;
  try {
    // Visible world rectangle (world Y-up).
    const a = vp.screenToWorld({ x: 0, y: 0 }), b = vp.screenToWorld({ x: vw, y: vh });
    const vLeft = Math.min(a.x, b.x), vRight = Math.max(a.x, b.x), vBottom = Math.min(a.y, b.y), vTop = Math.max(a.y, b.y);
    const seen = new Set<string>();
    for (const { bi, baseNaturalW } of items) {
      const id = bi.id;
      if (!id || !bi.pdfData || bi.rotation) continue; // rotated → base raster only
      seen.add(id);
      // If the current zoom is at or below the base raster's resolution, the base
      // is already crisp — drop any crop and skip.
      const baseDensity = baseNaturalW > 0 ? baseNaturalW / bi.widthMm : Infinity; // px per display-mm
      if (vp.scale <= baseDensity * 1.02) { crops.delete(id); continue; }

      // Intersect the image's world rect with the visible rect.
      const left = bi.x - bi.widthMm / 2, right = bi.x + bi.widthMm / 2;
      const top = bi.y + bi.heightMm / 2, bottom = bi.y - bi.heightMm / 2;
      const iLeft = Math.max(left, vLeft), iRight = Math.min(right, vRight);
      const iTop = Math.min(top, vTop), iBottom = Math.max(bottom, vBottom);
      if (iRight - iLeft < 0.5 || iTop - iBottom < 0.5) { crops.delete(id); continue; } // off-screen

      // Visible slice in normalised page coords (v grows downward from the top).
      const u0 = (iLeft - left) / bi.widthMm, u1 = (iRight - left) / bi.widthMm;
      const v0 = (top - iTop) / bi.heightMm, v1 = (top - iBottom) / bi.heightMm;
      // Full-page pixel size at the current zoom, then the crop within it.
      let fullW = bi.widthMm * vp.scale, fullH = bi.heightMm * vp.scale;
      let cw = (u1 - u0) * fullW, ch = (v1 - v0) * fullH;
      const cap = Math.min(1, MAX_CROP / cw, MAX_CROP / ch);
      if (cap < 1) { fullW *= cap; fullH *= cap; cw *= cap; ch *= cap; }
      cw = Math.max(1, Math.round(cw)); ch = Math.max(1, Math.round(ch));

      const key = `${Math.round(iLeft)},${Math.round(iTop)},${Math.round(iRight)},${Math.round(iBottom)}@${(vp.scale * cap).toFixed(3)}`;
      const prev = crops.get(id);
      if (prev && prev.key === key) continue; // view unchanged for this image

      const doc = await getDoc(bi.pdfData);
      const page = await doc.getPage(bi.pdfPage ?? 1);
      const pageW = page.getViewport({ scale: 1 }).width; // points
      const renderScale = fullW / pageW; // px per point
      const viewport = page.getViewport({ scale: renderScale });
      const canvas = document.createElement('canvas');
      canvas.width = cw; canvas.height = ch;
      const cctx = canvas.getContext('2d');
      if (!cctx) continue;
      // Offset the page so the visible slice's top-left lands at the canvas origin.
      // background: transparent — pdf.js defaults to an OPAQUE WHITE fill, which
      // would paint over the coloured base raster everywhere the page content
      // doesn't cover, turning the underlay white at zoom levels where this crisp
      // overlay is active. Transparent lets the base show through untouched, so
      // the overlay only ever adds sharpness, never a white-out.
      await page.render({ canvasContext: cctx, viewport, background: 'rgba(0,0,0,0)', transform: [1, 0, 0, 1, -u0 * fullW, -v0 * fullH] }).promise;
      crops.set(id, { canvas, x: (iLeft + iRight) / 2, y: (iBottom + iTop) / 2, widthMm: iRight - iLeft, heightMm: iTop - iBottom, key });
      changed = true;
    }
    // Forget crops for images no longer present.
    for (const id of [...crops.keys()]) if (!seen.has(id)) crops.delete(id);
  } catch { /* pdf.js failure — the base raster still shows */ }
  finally { running = false; }
  if (changed) onReady();
}
